import React, { useState, useEffect } from "react";
import CustomButton from "../components/common/CustomButton";
import CustomInput from "../components/common/CustomInput";
import { COLORS } from "../constants/colors.js";
import { FONTS } from "../constants/fonts.js";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { addEquipment, updateEquipment } from "../utils/equipmentUtils.js";
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";

const IMAGE_SIZE = 80;
const MARGIN = 8;
const IMAGES_PER_ROW = 4;
const TOTAL_WIDTH = "calc(100% - 48px)";

const styles = {
  container: {
    flex: 1,
    backgroundColor: COLORS.white,
    minHeight: "100vh",
  },
  content: {
    padding: "16px 24px",
    paddingTop: 16,
    maxWidth: 1400,
    margin: "0 auto",
  },
  title: {
    fontSize: 32,
    fontFamily: FONTS.bold,
    color: COLORS.text,
    fontWeight: 700,
    marginBottom: 24,
    animation: "fadeIn 0.5s ease-in-out",
  },
  sectionTitle: {
    fontSize: 18,
    fontFamily: FONTS.bold,
    color: COLORS.text,
    fontWeight: 600,
    marginBottom: 12,
  },
  imageSection: {
    marginBottom: 12,
  },
  imageRow: {
    display: "flex",
    flexWrap: "wrap",
    marginBottom: 8,
  },
  placeholder: {
    width: IMAGE_SIZE,
    height: IMAGE_SIZE,
    backgroundColor: `${COLORS.secondary}f0`,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    borderRadius: 8,
    border: `1px solid ${COLORS.tertiary}`,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginRight: MARGIN,
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: COLORS.primary,
      "& svg": {
        stroke: COLORS.white,
      },
    },
  },
  imageWrapper: {
    position: "relative",
    marginRight: MARGIN,
    marginBottom: MARGIN,
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
    },
  },
  image: {
    width: IMAGE_SIZE,
    height: IMAGE_SIZE,
    borderRadius: 8,
    objectFit: "cover",
  },
  deleteImageButton: {
    position: "absolute",
    top: -10,
    right: -10,
    backgroundColor: COLORS.error,
    borderRadius: 12,
    width: 24,
    height: 24,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "scale(1.1)",
    },
  },
  descriptionInput: {
    height: 120,
    marginBottom: 12,
  },
  saveButton: {
    marginTop: 12,
    marginBottom: 32,
  },
  "@keyframes fadeIn": {
    "0%": { opacity: 0, transform: "translateY(10px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
  "@media (max-width: 768px)": {
    content: {
      padding: "12px 16px",
      paddingTop: 16,
    },
    title: {
      fontSize: 28,
    },
    placeholder: {
      width: 64,
      height: 64,
    },
    image: {
      width: 64,
      height: 64,
    },
  },
};

const AddEquipmentScreen = () => {
  const { user, userType } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const equipment = location.state?.equipment;
  const [name, setName] = useState(equipment?.name || "");
  const [description, setDescription] = useState(equipment?.description || "");
  const [prices, setPrices] = useState({
    totalCost: equipment?.prices?.totalCost?.toString() || "",
    daily: equipment?.prices?.daily?.toString() || "",
    weekly: equipment?.prices?.weekly?.toString() || "",
    monthly: equipment?.prices?.monthly?.toString() || "",
  });
  const [images, setImages] = useState(equipment?.images || []);
  const [newImages, setNewImages] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user || userType !== "admin") {
      navigate("/admin");
    }
  }, [user, userType, navigate]);

  const handleImagePick = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      const newImageUrls = files.map((file) => URL.createObjectURL(file));
      setImages([...images, ...newImageUrls]);
      setNewImages([...newImages, ...files]);
    }
  };

  const handleDeleteImage = (index) => {
    const updatedImages = images.filter((_, i) => i !== index);
    const updatedNewImages = newImages.filter(
      (_, i) => i !== index - images.length + newImages.length
    );
    setImages(updatedImages);
    setNewImages(updatedNewImages);
  };

  const handlePriceChange = (key, value) => {
    setPrices((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (
      !name ||
      !description ||
      !prices.totalCost ||
      !prices.daily ||
      !prices.weekly
    ) {
      alert(
        "Please fill in all required fields (Name, Description, Total Cost, Daily Price, Weekly Price)"
      );
      return;
    }
    const totalCost = parseFloat(prices.totalCost);
    const daily = parseFloat(prices.daily);
    const weekly = parseFloat(prices.weekly);
    const monthly = prices.monthly ? parseFloat(prices.monthly) : null;
    if (
      isNaN(totalCost) ||
      totalCost < 0 ||
      isNaN(daily) ||
      daily < 0 ||
      isNaN(weekly) ||
      weekly < 0 ||
      (monthly !== null && (isNaN(monthly) || monthly < 0))
    ) {
      alert("Please enter valid prices");
      return;
    }
    setLoading(true);
    try {
      const equipmentData = {
        name,
        description,
        prices: {
          totalCost,
          daily,
          weekly,
          monthly,
        },
        images: images.filter((img) =>
          img.startsWith("https://firebasestorage.googleapis.com")
        ),
        createdAt: equipment ? equipment.createdAt : new Date().toISOString(),
      };
      let savedEquipment;
      if (equipment) {
        savedEquipment = await updateEquipment(equipment.id, {
          ...equipmentData,
          newImages,
        });
        alert("Equipment updated successfully");
      } else {
        savedEquipment = await addEquipment({
          ...equipmentData,
          newImages,
        });
        alert("Equipment added successfully");
      }
      setImages(savedEquipment.images || []);
      setNewImages([]);
      navigate("/admin/equipment");
    } catch (error) {
      alert("Failed to save equipment");
    } finally {
      setLoading(false);
    }
  };

  const renderImageRow = (rowImages, isFirstRow = false) => (
    <div style={styles.imageRow} key={Math.random()}>
      {isFirstRow && (
        <label style={styles.placeholder}>
          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleImagePick}
            multiple
          />
          <PlusIcon width={30} height={30} stroke={COLORS.primary} />
        </label>
      )}
      {rowImages.map((image, index) => {
        const globalIndex = images.indexOf(image);
        return (
          <div key={image} style={styles.imageWrapper}>
            <img src={image} alt="Equipment" style={styles.image} />
            <button
              style={styles.deleteImageButton}
              onClick={() => handleDeleteImage(globalIndex)}
            >
              <XMarkIcon width={20} height={20} stroke={COLORS.white} />
            </button>
          </div>
        );
      })}
    </div>
  );

  const renderImages = () => {
    const rows = [];
    const imagesPerRow = IMAGES_PER_ROW - 1;
    let currentIndex = 0;

    const firstRowImages = images.slice(0, imagesPerRow);
    rows.push(renderImageRow(firstRowImages, true));
    currentIndex = imagesPerRow;

    while (currentIndex < images.length) {
      const rowImages = images.slice(
        currentIndex,
        currentIndex + IMAGES_PER_ROW
      );
      rows.push(renderImageRow(rowImages));
      currentIndex += IMAGES_PER_ROW;
    }

    return rows;
  };

  if (!user || userType !== "admin") {
    return null;
  }

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <h1 style={styles.title}>
          {equipment ? "Edit Equipment" : "Add Equipment"}
        </h1>
        <div style={styles.imageSection}>
          <span style={styles.sectionTitle}>Images</span>
          {renderImages()}
        </div>
        <CustomInput
          value={name}
          onChangeText={setName}
          icon="label"
          placeholder="Enter equipment name"
          style={{ marginBottom: 12 }}
        />
        <CustomInput
          value={description}
          onChangeText={setDescription}
          icon="description"
          placeholder="Enter equipment description"
          multiline
          style={styles.descriptionInput}
        />
        <span style={styles.sectionTitle}>Pricing</span>
        <CustomInput
          label="Total Cost"
          value={prices.totalCost}
          onChangeText={(value) => handlePriceChange("totalCost", value)}
          icon="attach-money"
          placeholder="Enter total cost"
          type="number"
          style={styles.descriptionInput}
        />
        <CustomInput
          label="Daily Price"
          value={prices.daily}
          onChangeText={(value) => handlePriceChange("daily", value)}
          icon="attach-money"
          placeholder="Enter daily price"
          type="number"
          style={styles.descriptionInput}
        />
        <CustomInput
          label="Weekly Price"
          value={prices.weekly}
          onChangeText={(value) => handlePriceChange("weekly", value)}
          icon="attach-money"
          placeholder="Enter weekly price"
          type="number"
          style={styles.descriptionInput}
        />
        <CustomInput
          label="Monthly Price (Optional)"
          value={prices.monthly}
          onChangeText={(value) => handlePriceChange("monthly", value)}
          icon="attach-money"
          placeholder="Enter monthly price"
          type="number"
          style={styles.descriptionInput}
        />
        <CustomButton
          title={loading ? "Saving..." : equipment ? "Update" : "Add"}
          onPress={handleSave}
          style={styles.saveButton}
          disabled={loading}
        />
      </div>
    </div>
  );
};

export default AddEquipmentScreen;
